import { ChangeEvent, useState } from 'react';

import { Checkbox, InputLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AdminControlCheckbox } from '../../../utils/AdminControlCheckbox';
import { ApiKeyScope } from '../types';

const StyledScopesDiv = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 24px;
    background-color: #e5e5e5;
`;

const StyledScopeRow = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 10px;
`;

const StyledHr = styled.hr`
    width: 100%;
    border: 1px solid #e0e0e0;
`;

interface ScopeRowProps {
    field: string;
    writeChecked: boolean;
    readChecked: boolean;
    handleChange: (event) => void;
}

const ScopeRow: React.FC<ScopeRowProps> = ({ field, writeChecked, readChecked, handleChange }) => {
    const [checked, setChecked] = useState([writeChecked, readChecked]);

    const handleParentChange = (event: ChangeEvent<HTMLInputElement>) => {
        setChecked([event.target.checked, checked[1]]);
        handleChange(event);
    };

    const handleChildChange = (event: ChangeEvent<HTMLInputElement>) => {
        setChecked([checked[0], event.target.checked]);
        handleChange(event);
    };

    return (
        <StyledScopeRow>
            <AdminControlCheckbox
                label={`${field}:write`}
                control={<Checkbox checked={writeChecked} name={`${field}:write`} onChange={handleParentChange} />}
            />
            <AdminControlCheckbox
                label={`${field}:read`}
                sx={{ marginLeft: '18px' }}
                control={
                    <Checkbox
                        checked={readChecked || writeChecked}
                        name={`${field}:read`}
                        onChange={handleChildChange}
                        disabled={checked[0]}
                    />
                }
            />
        </StyledScopeRow>
    );
};

interface ScopeRowSingleProps {
    field: string;
    scopeType: string;
    scopeChecked: boolean;
    handleChange: (event) => void;
}

const ScopeRowSingle: React.FC<ScopeRowSingleProps> = ({ field, scopeType, scopeChecked, handleChange }) => {
    const [checked, setChecked] = useState(scopeChecked);

    const handleSingleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
        handleChange(event);
    };

    return (
        <StyledScopeRow>
            <AdminControlCheckbox
                label={`${field}:${scopeType}`}
                control={<Checkbox checked={checked} name={`${field}:${scopeType}`} onChange={handleSingleChange} />}
            />
        </StyledScopeRow>
    );
};

interface ScopesProps {
    scopes: ApiKeyScope[];
    handleChange: (event) => void;
}

const Scopes: React.FC<ScopesProps> = ({ scopes, handleChange }) => {
    const { t } = useTranslation('admin_settings');
    const fields = ['class', 'enrollment', 'schedule', 'template', 'user'];

    return (
        <StyledScopesDiv>
            <InputLabel sx={{ color: 'black' }}>{t('api-keys.scopes')}</InputLabel>
            <ScopeRowSingle
                field="attendance"
                scopeType="read"
                scopeChecked={scopes.includes(ApiKeyScope.ATTENDANCE_READ)}
                handleChange={handleChange}
            />
            <StyledHr />
            <ScopeRowSingle
                field="metrics"
                scopeType="read"
                scopeChecked={scopes.includes(ApiKeyScope.METRICS_READ)}
                handleChange={handleChange}
            />
            <StyledHr />
            {fields.map((field, index) => {
                const writeChecked = scopes.includes(ApiKeyScope[`${field.toUpperCase()}_WRITE`]);
                const readChecked = scopes.includes(ApiKeyScope[`${field.toUpperCase()}_READ`]);
                return (
                    <div key={index}>
                        <ScopeRow
                            handleChange={handleChange}
                            field={field}
                            writeChecked={writeChecked}
                            readChecked={readChecked}
                        />
                        {index < fields.length - 1 && <StyledHr />}
                    </div>
                );
            })}
        </StyledScopesDiv>
    );
};

export default Scopes;
